import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Download } from "lucide-react";
import { EXECUTIVE_OVERVIEW, PROPOSAL_PDF_URL, IMAGES } from "../content";
import { EASE } from "../motion";
import { Eyebrow, Logo } from "./Section";
import { usePanelA11y } from "../a11y";

export default function ExecutiveOverview({ open, onClose, onDiscuss }) {
  const ref = useRef(null);
  usePanelA11y(open, ref, onClose);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="executive-overview"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45 }}
          className="fixed inset-0 z-[105] overflow-y-auto bg-[#242424]"
        >
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img src={IMAGES.botanical} alt="" className="w-full h-full object-cover opacity-10" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#242424]/60 via-[#242424]/80 to-[#242424]" />
          </div>

          <div ref={ref} role="dialog" aria-modal="true" aria-label="Executive overview" className="relative max-w-2xl mx-auto px-6 sm:px-8 py-24 sm:py-28 min-h-full flex flex-col">
            <div className="flex items-start justify-between gap-6">
              <Logo dark />
              <button
                data-testid="overview-close"
                onClick={onClose}
                aria-label="Close executive overview"
                className="p-2 -m-2 text-[#F5F3EE]/50 hover:text-[#F5F3EE] transition-colors duration-300 cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mt-14">
              <Eyebrow light>Executive overview · two minutes</Eyebrow>
              <h2 className="font-serif font-light text-3xl sm:text-4xl leading-[1.15] tracking-tight mt-5 text-[#F5F3EE]">
                The proposal, distilled, with the full experience waiting beneath it.
              </h2>
            </div>

            <ol className="mt-12 border-t border-[#F5F3EE]/12">
              {EXECUTIVE_OVERVIEW.map((item, i) => (
                <motion.li
                  key={item.title}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, ease: EASE, delay: 0.15 + i * 0.07 }}
                  className="py-6 border-b border-[#F5F3EE]/10"
                >
                  <p className="flex items-baseline gap-4">
                    <span className="font-serif italic text-lg text-[#FFD400]/80 w-6 shrink-0">0{i + 1}</span>
                    <span className="text-sm font-semibold tracking-wide text-[#F5F3EE]">{item.title}</span>
                  </p>
                  <p className="text-sm leading-relaxed text-[#F5F3EE]/60 mt-2 pl-10">{item.text}</p>
                </motion.li>
              ))}
            </ol>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="overview-discuss-button"
                onClick={onDiscuss}
                className="inline-flex items-center gap-2.5 bg-[#FFD400] text-[#0F0F0F] text-[11px] font-bold uppercase tracking-[0.18em] px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-[#E6BE00] hover:-translate-y-0.5 cursor-pointer"
              >
                Discuss the Project
                <ArrowUpRight size={13} strokeWidth={2.5} />
              </button>
              <a
                data-testid="overview-download-pdf"
                href={PROPOSAL_PDF_URL}
                download
                className="inline-flex items-center gap-2.5 text-[#F5F3EE]/75 text-[11px] font-bold uppercase tracking-[0.18em] px-8 py-4 rounded-full border border-[#F5F3EE]/25 transition-[background-color,border-color,color] duration-300 hover:border-[#F5F3EE]/60 hover:text-[#F5F3EE] cursor-pointer"
              >
                <Download size={13} strokeWidth={2.5} />
                Download Formal Proposal
              </a>
            </motion.div>

            <p className="text-xs text-[#F5F3EE]/40 leading-relaxed mt-10">
              Close this overview at any point to return to exactly where you were in the full experience.
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
